/* Filter contextual Liquid variant prices without altering catalog or market prices. */
if (!customElements.get('artvet-context-price')) {
  customElements.define('artvet-context-price', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      queueMicrotask(() => this.start());
    }
    disconnectedCallback() { this.abort?.abort(); }
    start() {
      if (!this.isConnected) return;
      this.wrapper = this.closest('.collection-wrapper');
      this.form = this.querySelector('form');
      this.status = this.querySelector('[role="status"]');
      window.ArtVetFacets?.enhance(this);
      const url = new URL(location.href);
      for (const name of ['av_price_min', 'av_price_max']) this.form.elements[name].value = url.searchParams.get(name) || '';
      const clear = this.querySelector('.av-price-clear');
      const clean = new URL(url);
      for (const name of ['av_price_min', 'av_price_max', 'av_page', 'page', 'filter.v.price.gte', 'filter.v.price.lte']) clean.searchParams.delete(name);
      clear.href = clean.href;
      clear.hidden = !url.searchParams.has('av_price_min') && !url.searchParams.has('av_price_max');
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        const target = new URL(clean);
        const min = this.form.elements.av_price_min;
        const max = this.form.elements.av_price_max;
        max.setCustomValidity('');
        if (min.value !== '' && max.value !== '' && Number(min.value) > Number(max.value)) {
          max.setCustomValidity(this.dataset.error); max.reportValidity(); return;
        }
        for (const input of [min, max]) if (input.value !== '') target.searchParams.set(input.name, input.value);
        location.assign(target.href);
      }, {signal: this.abort.signal});
      this.form.elements.av_price_max.addEventListener('input', () => this.form.elements.av_price_max.setCustomValidity(''), {signal: this.abort.signal});
      const active = url.searchParams.has('av_price_min') || url.searchParams.has('av_price_max');
      if (active || /^price-/.test(url.searchParams.get('sort_by') || '')) this.load(url, active);
    }
    async load(url, active) {
      const signal = this.abort.signal;
      this.status.textContent = this.dataset.loading;
      this.wrapper.classList.add('av-price-pending');
      try {
        const source = new URL(url);
        for (const key of ['av_price_min', 'av_price_max', 'av_page', 'page', 'filter.v.price.gte', 'filter.v.price.lte']) source.searchParams.delete(key);
        source.searchParams.set('section_id', 'artvet-context-price-data');
        let next = source.href;
        const cards = new Map();
        const visited = new Set();
        while (next && !visited.has(next)) {
          visited.add(next);
          const response = await fetch(next, {signal, credentials:'same-origin'});
          if (!response.ok) throw new Error('price-data');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const root = doc.querySelector('[data-artvet-price-data]');
          const template = root?.querySelector('template');
          if (!root || !template) throw new Error('price-data');
          for (const card of template.content.querySelectorAll('[data-artvet-product]')) cards.set(card.dataset.artvetProduct, card);
          next = root.dataset.next ? new URL(root.dataset.next, source).href : '';
          if (next) { const link = new URL(next); link.searchParams.set('section_id', 'artvet-context-price-data'); next = link.href; }
        }
        if (signal.aborted || !this.isConnected) return;
        const min = url.searchParams.get('av_price_min');
        const max = url.searchParams.get('av_price_max');
        const lower = min === null || min === '' ? 0 : Math.round(Number(min) * 100);
        const upper = max === null || max === '' ? Infinity : Math.round(Number(max) * 100);
        const availability = url.searchParams.getAll('filter.v.availability');
        const available = availability.length === 1 ? availability[0] === '1' : null;
        const matches = [...cards.values()].filter(card => JSON.parse(card.dataset.artvetPrices).some(variant =>
          variant.price >= lower && variant.price <= upper && (available === null || variant.available === available)));
        const sort = url.searchParams.get('sort_by');
        if (/^price-/.test(sort || '')) {
          const price = card => Math.min(...JSON.parse(card.dataset.artvetPrices).map(variant => variant.price));
          matches.sort((a,b) => (price(a) - price(b)) * (sort === 'price-descending' ? -1 : 1));
        }
        const grid = this.wrapper.querySelector('#collection');
        if (!grid && matches.length === 0) {
          this.status.textContent=this.dataset.results.replace('[count]','0');
          for (const element of this.wrapper.querySelectorAll('.f8sr .hx')) element.textContent=this.status.textContent;
          return;
        }
        if (!grid) throw new Error('price-grid');
        // Preserve Rover's normal grid/list wrapper; fetched cards use its original product snippet.
        for (const element of this.wrapper.querySelectorAll('.n6pg, .pagination, [data-pagination]')) element.classList.add('av-price-native-pagination');
        const sibling = grid.nextElementSibling;
        if (sibling && !sibling.contains(this) && !sibling.matches('script')) sibling.classList.add('av-price-native-pagination');
        for (const element of this.wrapper.querySelectorAll('.f8sr .hx')) element.textContent=this.dataset.results.replace('[count]',String(matches.length));
        for (const element of this.wrapper.querySelectorAll('#filter .submit a[aria-controls="filter"] > span')) element.textContent=`(${matches.length})`;
        this.wrapper.classList.add('av-price-active');
        const pages = Math.max(1, Math.ceil(matches.length / Number(this.dataset.pageSize || 12)));
        this.page = Math.min(pages, Math.max(1, Number(url.searchParams.get('av_page')) || 1));
        const nav = document.createElement('nav');
        nav.className = 'av-price-pages';
        nav.setAttribute('aria-label', this.dataset.page);
        const previous = document.createElement('button'); previous.type='button'; previous.textContent=this.dataset.previous;
        const label = document.createElement('span');
        const following = document.createElement('button'); following.type='button'; following.textContent=this.dataset.next;
        nav.append(previous,label,following);
        grid.after(nav);
        const render = () => {
          const start = (this.page - 1) * Number(this.dataset.pageSize || 12);
          grid.replaceChildren(...matches.slice(start,start + Number(this.dataset.pageSize || 12)).map(card => card.cloneNode(true)));
          if (!matches.length) { const empty=document.createElement('li'); empty.textContent=this.dataset.empty; grid.append(empty); }
          label.textContent = `${this.dataset.page} ${this.page} / ${pages}`;
          previous.disabled=this.page===1; following.disabled=this.page===pages; nav.hidden=pages===1;
          this.status.textContent=this.dataset.results.replace('[count]',String(matches.length));
          if (typeof quickShop !== 'undefined') quickShop.init();
          if (typeof ajaxCart !== 'undefined') ajaxCart.init();
          if (typeof productcardVariantsEvt !== 'undefined') window.dispatchEvent(productcardVariantsEvt);
        };
        previous.addEventListener('click',()=>{this.page--;render();grid.scrollIntoView({block:'start'});},{signal});
        following.addEventListener('click',()=>{this.page++;render();grid.scrollIntoView({block:'start'});},{signal});
        render();
      } catch (error) {
        if (!signal.aborted) {
          // Never show an unfiltered list as if the price filter succeeded.
          this.status.textContent=this.dataset.error;
          if (active) { const grid=this.wrapper.querySelector('#collection'); if(grid) grid.replaceChildren(); }
        }
      } finally { this.wrapper?.classList.remove('av-price-pending'); }
    }
  });
}
